import type { CSSProperties, ReactNode } from 'react';

export interface MarqueeProps {
  /** Items shown in the band (strings or nodes; keep them short). */
  items: ReactNode[];
  /** Accessible name of the list. */
  label?: string;
  /** Seconds for one full loop. */
  duration?: number;
  /** Scroll direction. */
  direction?: 'left' | 'right';
  /** Copies of the item list per half, so the band is wider than wide screens. */
  repeat?: number;
  /** Gap between items (CSS length). */
  gap?: string;
  className?: string;
  itemClassName?: string;
  /** Optional element rendered between items (decorative). */
  separator?: ReactNode;
}

/** Infinite seamless band that pauses on hover/focus; only the first copy is exposed to assistive tech. */
export default function Marquee({
  items,
  label,
  duration = 40,
  direction = 'left',
  repeat = 2,
  gap,
  className = '',
  itemClassName = '',
  separator,
}: MarqueeProps) {
  const copies = Math.max(1, repeat);
  const style = {
    '--marquee-dur': `${duration}s`,
    ...(gap ? { '--marquee-gap': gap } : null),
  } as CSSProperties;

  const renderGroup = (half: number) =>
    Array.from({ length: copies }).map((_, copy) => {
      const hidden = half > 0 || copy > 0;
      return items.map((item, i) => (
        <li key={`${half}-${copy}-${i}`} className={itemClassName} aria-hidden={hidden || undefined}>
          {item}
          {separator ? <span className="marquee-sep" aria-hidden="true">{separator}</span> : null}
        </li>
      ));
    });

  return (
    <div className={`marquee ${className}`} data-direction={direction} style={style}>
      <div className="marquee-track">
        <ul className="marquee-group" aria-label={label}>
          {renderGroup(0)}
        </ul>
        <ul className="marquee-group" aria-hidden="true">
          {renderGroup(1)}
        </ul>
      </div>
    </div>
  );
}
